// timeSheetServices.js — RTK Query hooks for the Time Sheet page.
//
// All endpoints hit /timesheet/portal/... (mounted in Portal-BFF timesheetRoute.js).
// Auth is the shared Bearer token via prepareHeaders, same as the other services.
// The page itself is President-gated in the BFF controller; this layer only
// carries the request.

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

// Filters are arrays (Teams, Positions) and the API accepts repeated params, so
// they are serialised by hand rather than through RTK's params object.
//
// THE PARAM NAMES ARE THE SERVER'S, NOT THIS FILE'S. timesheetController.handleList
// reads `team`, `position`, `employee` and `open` — singular, snake-free. Sending
// the plural spellings would be accepted by Express, ignored by the handler, and
// return an unfiltered page with no error anywhere: the filter would simply look
// broken. Same for the body keys below (`clock_in`, not `clockIn`).
const toSearchParams = (filters = {}) => {
  const sp = new URLSearchParams();
  const add = (key, value) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) value.filter(Boolean).forEach((v) => sp.append(key, v));
    else sp.append(key, value);
  };
  add("from", filters.from);
  add("to", filters.to);
  add("q", filters.q);
  add("team", filters.teams);
  add("position", filters.positions);
  add("employee", filters.employeeRecids);
  if (filters.onlyOpen) add("open", "true");
  add("limit", filters.limit);
  add("offset", filters.offset);
  return sp.toString();
};

export const timeSheetApi = createApi({
  reducerPath: "timeSheetApi",
  tagTypes: ["TimeSheetRecords", "TimeSheetOptions"],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}/timesheet/portal`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth?.token;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // Filter vocabulary: teams, positions, and the employee roster. Read from the
    // same table the records join to, so every option matches at least one row.
    getTimeSheetOptions: builder.query({
      query: () => "/options",
      transformResponse: (res) => res?.data || {},
      providesTags: ["TimeSheetOptions"],
    }),

    // One filtered slice of the log. The response carries the rows, the window
    // total, and a per-employee rollup computed over the SAME filter, so the
    // summary strip can never describe a different set than the list under it.
    //
    // Every response is wrapped: `{ success, data }`. transformResponse unwraps
    // it here rather than at each call site, so the component reads
    // `records.data.rows` and not `records.data.data.records`.
    getTimeSheetRecords: builder.query({
      query: (filters) => `/records?${toSearchParams(filters)}`,
      transformResponse: (res) => res?.data || {},
      providesTags: ["TimeSheetRecords"],
    }),

    getTimeSheetRecord: builder.query({
      query: (recid) => `/records/${recid}`,
      transformResponse: (res) => res?.data || {},
      providesTags: ["TimeSheetRecords"],
    }),

    // Edit one punch. Partial: only the keys present are changed.
    // Body keys are the server's snake_case (handleUpdate maps them to Airtable
    // field names); sending camelCase would be silently dropped and the save
    // would report success having changed nothing.
    updateTimeSheetRecord: builder.mutation({
      query: ({ recid, clockIn, clockOut, notes, dateOverride, lostReliabilityBonus }) => ({
        url: `/records/${recid}`,
        method: "PATCH",
        body: {
          ...(clockIn !== undefined ? { clock_in: clockIn } : {}),
          ...(clockOut !== undefined ? { clock_out: clockOut } : {}),
          ...(notes !== undefined ? { notes } : {}),
          ...(dateOverride !== undefined ? { date_override: dateOverride } : {}),
          ...(lostReliabilityBonus !== undefined
            ? { lost_reliability_bonus: lostReliabilityBonus }
            : {}),
        },
      }),
      invalidatesTags: ["TimeSheetRecords"],
    }),

    // Add a punch for an employee.
    createTimeSheetRecord: builder.mutation({
      query: ({ employeeRecid, clockIn, clockOut, notes, type }) => ({
        url: "/records",
        method: "POST",
        body: {
          employee_recid: employeeRecid,
          clock_in: clockIn,
          clock_out: clockOut,
          notes,
          type,
        },
      }),
      invalidatesTags: ["TimeSheetRecords"],
    }),

    // Remove a punch. Returns the deleted recid.
    deleteTimeSheetRecord: builder.mutation({
      query: (recid) => ({ url: `/records/${recid}`, method: "DELETE" }),
      invalidatesTags: ["TimeSheetRecords"],
    }),
  }),
});

export const {
  useGetTimeSheetOptionsQuery,
  useGetTimeSheetRecordsQuery,
  useGetTimeSheetRecordQuery,
  useUpdateTimeSheetRecordMutation,
  useCreateTimeSheetRecordMutation,
  useDeleteTimeSheetRecordMutation,
} = timeSheetApi;

export { toSearchParams };
